import Vue from "vue";
import deepmerge from "deepmerge";
import set from "lodash/set";
import get from "lodash/get";
import { _isMobile } from "../utils/helpers";
import * as ERRORES from "../../config/constants/step-constants";
import configBrand from "../../config";

const isProduction = process.env.NODE_ENV === "production";

const _normalizeCharacters = str => {
  return str
    ? (str + "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
    : str;
};

const AdobeFunctions = {
  internal(context, _callback, clearVars = false) {
    if (clearVars) {
      AdobeFunctions.clearVars();
    }
    AdobeFunctions.setConstants();
    AdobeFunctions.setOptimizations(context);
    setTimeout(_callback, 0);
  },
  callAdobeDTM(method, event) {
    /**
     * Realizar llamada al DTM
     */
    if (event !== "Page View") {
      // No se debe reportar los props de application para page views
      AdobeFunctions.setApplicationProps();
    }
    try {
      window[method](event);
    } catch (error) {
      Vue.$log.error("🚀 Adobe Launch error: ", error);
    }
    Vue.$log.info("🚀 Adobe Launch call: ", event);
  },
  clearVars() {
    /**
     * Limpiar las variables cuando hace falta
     */
    set(window.digitalData, "products.attributes", []);
    set(window.digitalData, "internalCampaign.attributes", []);
    set(window.digitalData, "application.state", "");
    set(window.digitalData, "application.step", "");
    set(window.digitalData, "application.isQualifiedVisits", "");
    set(window.digitalData, "application.errorType", "");
    set(window.digitalData, "page.pageInfo.errorPage", "");
    set(window.digitalData, "page.pageInfo.level2", "");
    set(window.digitalData, "page.pageInfo.pageName", "");
    set(window.digitalData, "application.typology", "");
    set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "",
          name: ""
        },
        fulfillmentModel: ""
      })
    );
  },
  setPageInstanceId() {
    /**
     * Establecer entorno en datalayer
     */
    set(window.digitalData, "pageInstanceID", isProduction ? "pro" : "de");
  },
  setIsMobile() {
    if (_isMobile()) {
      set(window.digitalData, "user.device.mobile", "si");
    } else {
      set(window.digitalData, "user.device.mobile", "no");
    }
  },
  setSysEnv() {
    if (_isMobile()) {
      set(window.digitalData, "page.pageInfo.sysEnv", "web-movil");
    } else {
      set(window.digitalData, "page.pageInfo.sysEnv", "escritorio");
    }
  },
  setPageName(pageName) {
    let segments = [
      get(window.digitalData, "page.pageInfo.sysEnv", "escritorio"),
      "publica",
      "general",
      "contacto empresa"
    ];
    if (pageName) {
      segments.push(pageName);
      set(window.digitalData, "page.pageInfo.level2", pageName);
    }
    set(window.digitalData, "page.pageInfo.pageName", segments.join(":"));
  },
  setConstants() {
    set(window.digitalData, "page.pageInfo.server", window.location.hostname);
    set(window.digitalData, "user.device.userAgent", navigator.userAgent);
    set(window.digitalData, "page.pageInfo.pageIntent", "transaccion");
    AdobeFunctions.setPageInstanceId();
    AdobeFunctions.setIsMobile();
    AdobeFunctions.setSysEnv();
  },
  setOptimizations(context) {
    if (context && context.state && context.state.adobeTargetExperience) {
      /**
       * Se especificó una experiencia de Target. Indicarla al tagging.
       * La experiencia DEBE venir de Target con el formato: `IDACTIVIDAD_EXPERIENCIA`
       * Ej: "AR072019_B"
       */
      let datosTarget = context.state.adobeTargetExperience.split("_");
      if (datosTarget.length === 2) {
        let attributes = [
          {
            idOptimization: datosTarget[0],
            experience: datosTarget[1],
            place: "",
            type: "",
            executor: "Target",
            audience: ""
          }
        ];
        set(window.digitalData, "optimization.attributes", attributes);
      }
    }
  },
  setInternalCampaign() {
    let attributes = [
      {
        location: "",
        campaignFormat: "",
        collectiveCode: "",
        campaignName: "",
        product: "",
        productCode: "",
        quantity: ""
      }
    ];
    set(window.digitalData, "internalCampaign.attributes", attributes);
  },
  setProductAttributes(oferta) {
    let attributes = [];
    let attributesModel = {
      primaryCategory: "",
      productSubtype: "",
      productName: "",
      productCode: "",
      quantity: "",
      amount: "",
      paymentAmount: "",
      numberOfPayments: "",
      paymentDate: "",
      paymentType: "",
      serviceCharge: "",
      currency: "",
      numberOfHolders: "",
      interestRate: {
        tin: "",
        tae: "",
        rate: ""
      },
      term: "",
      group: "",
      state: ""
    };

    let montoMaximo = get(oferta, "respuesta.oferta.montoMaximo", null);
    let montoLargoPlazo = get(oferta, "respuesta.oferta.montoLargoPlazo", null);
    let isClient = get(oferta, "respuesta.oferta.esCliente", false);
    const __addProduct = (producto, amount = "") => {
      if (get(producto, "productName", "")) {
        attributes.push(
          deepmerge(attributesModel, {
            primaryCategory: get(producto, "primaryCategory", ""),
            productSubtype: get(producto, "productSubtype", ""),
            productName: get(producto, "productName", ""),
            quantity: "1",
            currency: "ARS",
            amount: amount,
            group: ""
          })
        );
      }
    };

    if (montoMaximo) {
      get(
        configBrand.get(),
        `ofertaItems[${isClient ? "cliente" : "noCliente"}]`,
        []
      ).forEach(producto => {
        __addProduct(
          producto,
          get(producto, "primaryCategory", "") === "prestamos"
            ? montoMaximo
            : ""
        );
      });
    }
    if (montoLargoPlazo) {
      get(configBrand.get(), "ofertaItems.checkItemsLargoPlazo", []).forEach(
        producto => {
          __addProduct(producto, montoLargoPlazo);
        }
      );
    }

    set(window.digitalData, "products.attributes", attributes);
  },
  setProductState(state) {
    get(window.digitalData, "products.attributes", []).forEach(
      (product, index) => {
        set(window.digitalData.products.attributes, `[${index}].state`, state);
      }
    );
  },
  setApplicationProps() {
    set(
      window.digitalData,
      "application",
      deepmerge(window.digitalData.application, {
        application: {
          type: "formulario",
          name: `contacto empresa`
        },
        fulfillmentModel: "online"
      })
    );
  }
};

const Adobe = {
  enable() {},
  wait() {
    return !!window._satellite && !!(document.readyState === "complete");
  },
  init() {
    AdobeFunctions.internal(
      undefined,
      () => {
        Vue.$log.info("🚀 Adobe Launch working");
      },
      true
    );
  },
  appStartedClick(context) {
    AdobeFunctions.internal(context, () => {
      set(
        window.digitalData,
        "application.step",
        _normalizeCharacters(context.mutation.payload)
      );
      set(window.digitalData, "application.isQualifiedVisits", "");
      AdobeFunctions.callAdobeDTM("digitalLink", "App Started");
    });
  },
  appOnClickStart(context) {
    AdobeFunctions.internal(context, () => {
      set(
        window.digitalData,
        "application.step",
        _normalizeCharacters(context.mutation.payload)
      );
      AdobeFunctions.callAdobeDTM("digitalLink", "App On Click Start");
    });
  },
  paso1(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("1 identificacion empresa");
      set(
        window.digitalData,
        "page.pageInfo.level2",
        "1 identificacion empresa"
      );
      set(window.digitalData, "application.step", "1 identificacion empresa");
      set(window.digitalData, "application.isQualifiedVisits", "false");

      AdobeFunctions.callAdobeDTM("digitalView", "App Page Visit");
    });
  },
  paso2(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.clearVars();
      AdobeFunctions.setPageName("2 datos empresa");
      set(window.digitalData, "page.pageInfo.level2", "2 datos empresa");
      set(window.digitalData, "application.step", "2 datos empresa");
      set(
        window.digitalData,
        "application.typology",
        Number(context.state.paso1.cuit.split("-")[0]) < 30
          ? "emprendedores"
          : "empresas"
      );

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 2");
    });
  },
  paso3(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("3 datos contacto");
      set(window.digitalData, "page.pageInfo.level2", "3 datos contacto");
      set(window.digitalData, "application.step", "3 datos contacto");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 3");
    });
  },
  paso4(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("4 sucursal");
      set(window.digitalData, "page.pageInfo.level2", "4 sucursal");
      set(window.digitalData, "application.step", "4 sucursal");

      AdobeFunctions.callAdobeDTM("digitalView", "App Step 4");
    });
  },
  gracias(context) {
    AdobeFunctions.internal(context, () => {
      AdobeFunctions.setPageName("pagina exitosa");
      set(window.digitalData, "page.pageInfo.level2", "pagina exitosa");
      set(window.digitalData, "application.step", "pagina exitosa");
      set(window.digitalData, "application.state", "finalizado");

      AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
    });
  },
  mensaje(context) {
    AdobeFunctions.internal(
      context,
      () => {
        // Para los errores se disparan eventos diferentes
        // de acuerdo al error por lo que en cada case debe llamarse a adobeDTM
        // Huella avanzada: errores que puede volver
        // app completed: finaliza el proceso y no puede volver
        AdobeFunctions.clearVars();
        set(window.digitalData, "application.globalApplication", "");
        switch (context.mutation.payload.params.mje) {
          case ERRORES.INDIVIDUO_NEGATIVO:
            AdobeFunctions.setPageName("pagina exitosa");
            set(window.digitalData, "page.pageInfo.level2", "pagina exitosa");
            set(window.digitalData, "application.step", "pagina exitosa");
            set(window.digitalData, "application.state", "rechazado");
            set(
              window.digitalData,
              "user.segment.profile",
              "empresa ya registrada"
            );
            set(
              window.digitalData,
              "application.typology",
              Number(context.state.paso1.cuit.split("-")[0]) < 30
                ? "emprendedores"
                : "empresas"
            );

            AdobeFunctions.callAdobeDTM("digitalView", "App Completed");
            break;
          default:
            AdobeFunctions.setPageName("error tecnico");
            set(window.digitalData, "page.pageInfo.level2", "error tecnico");
            set(window.digitalData, "page.pageInfo.errorPage", "error tecnico");
            set(window.digitalData, "page.pageInfo.pageIntent", "informacion");

            AdobeFunctions.callAdobeDTM("digitalView", "Page View");
            break;
        }
      },
      true
    );
  }
};

export default Adobe;
